import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Image as ImageIcon, Film, X, Check } from "lucide-react";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";

const TAGS = [
  "streetwear","casual","old money","luxury","business casual","smart casual",
  "athletic","minimalist","vintage","y2k","techwear","formal","trending",
];
const GENDERS = [
  { id: "men", label: "Men's" },
  { id: "women", label: "Women's" },
  { id: "unisex", label: "Unisex" },
];
const CONTENT_TYPES = [
  { id: "fit_check", label: "Fit check" },
  { id: "transformation", label: "Transformation" },
  { id: "styling_tip", label: "Styling tip" },
  { id: "haul", label: "Shopping haul" },
  { id: "other", label: "Other" },
];

// Cap raw video at 12 MB so base64 stays under ~16 MB.
const MAX_VIDEO_BYTES = 12 * 1024 * 1024;
// Cap video duration at 60 seconds.
const MAX_VIDEO_SECONDS = 60;

async function fileToResizedImageBase64(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s); height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject(new Error("decode"));
      img.src = r.result;
    };
    r.onerror = () => reject(new Error("read"));
    r.readAsDataURL(file);
  });
}

function fileToBase64DataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("read"));
    r.readAsDataURL(file);
  });
}

async function videoFirstFrameThumbnail(file, maxDim = 720) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    let duration = 0;
    const cleanup = () => { URL.revokeObjectURL(url); };
    video.addEventListener("loadedmetadata", () => {
      duration = video.duration || 0;
      video.currentTime = Math.min(0.2, duration / 2 || 0.2);
    });
    video.addEventListener("seeked", () => {
      try {
        let { videoWidth: w, videoHeight: h } = video;
        const s = Math.min(1, maxDim / Math.max(w, h));
        w = Math.round(w * s); h = Math.round(h * s);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(video, 0, 0, w, h);
        const dataUrl = c.toDataURL("image/jpeg", 0.82);
        cleanup();
        resolve({ thumbnail: dataUrl, duration });
      } catch {
        cleanup();
        resolve({ thumbnail: null, duration });
      }
    });
    video.addEventListener("error", () => { cleanup(); resolve({ thumbnail: null, duration: 0 }); });
  });
}

export default function FashionFeedNewScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const photoCamRef = useRef(null);
  const photoGalRef = useRef(null);
  const videoRef = useRef(null);

  const [media, setMedia] = useState(null); // { type, dataUrl, thumbnail, mime, durationSec }
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState([]);
  const [gender, setGender] = useState(null);
  const [contentType, setContentType] = useState("fit_check");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onPhotoPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/image\/(jpeg|jpg|png|webp)/i.test(f.type)) {
      toast.show("Image only.", "error");
      return;
    }
    try {
      const dataUrl = await fileToResizedImageBase64(f, 1280);
      setMedia({ type: "image", dataUrl, thumbnail: dataUrl, mime: "image/jpeg" });
    } catch {
      toast.show("Could not read image.", "error");
    }
  };

  const onVideoPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/video\/(mp4|quicktime|webm)/i.test(f.type)) {
      toast.show("Video only (mp4 / mov / webm).", "error");
      return;
    }
    if (f.size > MAX_VIDEO_BYTES) {
      toast.show("Video too large. Max 12MB.", "error");
      return;
    }
    try {
      const [{ thumbnail, duration }, dataUrl] = await Promise.all([
        videoFirstFrameThumbnail(f),
        fileToBase64DataUrl(f),
      ]);
      if (duration > MAX_VIDEO_SECONDS + 0.5) {
        toast.show(`Video must be ${MAX_VIDEO_SECONDS}s or less.`, "error");
        return;
      }
      setMedia({
        type: "video",
        dataUrl,
        thumbnail,
        mime: f.type || "video/mp4",
        durationSec: duration,
      });
    } catch {
      toast.show("Could not read video.", "error");
    }
  };

  const toggleTag = (t) =>
    setTags((p) => (p.includes(t) ? p.filter((x) => x !== t) : p.length >= 6 ? p : [...p, t]));

  const submit = async () => {
    if (!media) { setError("Add a photo or video first."); return; }
    setSaving(true); setError("");
    try {
      const stripData = (s) => (s ? s.split(",", 2)[1] : null);
      const body =
        media.type === "video"
          ? {
              media_type: "video",
              video_base64: stripData(media.dataUrl),
              video_mime: media.mime,
              thumbnail_base64: stripData(media.thumbnail),
              thumbnail_mime: "image/jpeg",
              caption: caption.trim(),
              style_tags: tags,
              gender_tag: gender,
              content_type_tag: contentType,
            }
          : {
              media_type: "image",
              image_base64: stripData(media.dataUrl),
              image_mime: "image/jpeg",
              caption: caption.trim(),
              style_tags: tags,
              gender_tag: gender,
              content_type_tag: contentType,
            };
      await api.post("/feed/posts", body);
      toast.show("Posted!", "success");
      setTimeout(() => navigate("/feed"), 300);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen fade-in" data-testid="feed-new-screen">
      <TopBar title="NEW POST" />
      <div style={{ marginBottom: 18 }}>
        <h2 className="h1" style={{ fontSize: 32 }}>
          Drop <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>a fit</em>
        </h2>
        <p className="body" style={{ marginTop: 10 }}>
          Upload a vertical video (9:16) or a single photo. Add tags so the right people find it.
        </p>
      </div>

      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>MEDIA</div>
        {media ? (
          <div style={{ position: "relative" }}>
            {media.type === "video" ? (
              <video
                src={media.dataUrl}
                poster={media.thumbnail || undefined}
                controls
                playsInline
                className="preview-img"
                style={{ maxHeight: 420, width: "100%", borderRadius: 14, background: "#0a0a0a" }}
                data-testid="feed-new-preview-video"
              />
            ) : (
              <img
                src={media.dataUrl}
                alt="post"
                className="preview-img"
                style={{ maxHeight: 420, borderRadius: 14 }}
                data-testid="feed-new-preview"
              />
            )}
            <button
              type="button"
              className="icon-btn"
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setMedia(null)}
              data-testid="feed-new-clear"
            >
              <X size={14}/>
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={() => videoRef.current?.click()} data-testid="feed-new-video-btn">
              <Film size={14}/> Upload video
            </button>
            <button className="btn btn-outline" onClick={() => photoCamRef.current?.click()} data-testid="feed-new-camera-btn">
              <Camera size={14}/> Take photo
            </button>
            <button className="btn btn-outline" onClick={() => photoGalRef.current?.click()} data-testid="feed-new-gallery-btn">
              <ImageIcon size={14}/> From gallery
            </button>
            <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={onVideoPicked} data-testid="feed-new-video-input"/>
            <input ref={photoCamRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={onPhotoPicked} data-testid="feed-new-camera-input"/>
            <input ref={photoGalRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onPhotoPicked} data-testid="feed-new-gallery-input"/>
          </div>
        )}
        <div className="tiny" style={{ color: "rgba(255,255,255,0.4)", marginTop: 8 }}>
          Videos: max 60s, 12MB. Vertical 9:16 recommended.
        </div>
      </div>

      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>CONTENT TYPE</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CONTENT_TYPES.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`chip ${contentType === c.id ? "active" : ""}`}
              onClick={() => setContentType(c.id)}
              data-testid={`feed-new-ctype-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>CAPTION</div>
        <textarea
          className="input" rows={3} placeholder="What's the vibe?"
          value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 600))}
          data-testid="feed-new-caption"
        />
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-faint)", lineHeight: 1.5 }}>
          By posting, you agree to Nova&apos;s&nbsp;
          <a
            href="/legal/community-guidelines"
            target="_blank"
            rel="noreferrer"
            data-testid="feed-new-guidelines-link"
            style={{ color: "var(--gold)" }}
          >
            Community Guidelines
          </a>
          . Posts violating them may be removed.
        </div>
      </div>

      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>STYLE TAGS · {tags.length}/6</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TAGS.map((t) => (
            <button
              key={t}
              type="button"
              className={`chip ${tags.includes(t) ? "active" : ""}`}
              onClick={() => toggleTag(t)}
              data-testid={`feed-new-tag-${t.replace(/\s+/g, "-")}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>AUDIENCE (OPTIONAL)</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {GENDERS.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`chip ${gender === g.id ? "active" : ""}`}
              onClick={() => setGender(gender === g.id ? null : g.id)}
              data-testid={`feed-new-gender-${g.id}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-text" data-testid="feed-new-error" style={{ marginBottom: 12 }}>{error}</div>}

      <button
        className="btn btn-primary"
        onClick={submit}
        disabled={saving || !media}
        data-testid="feed-new-submit"
        style={{ marginBottom: 24 }}
      >
        {saving ? (<><div className="spinner small"/> Posting…</>) : (<><Check size={14}/> Post fit</>)}
      </button>
    </div>
  );
}
